// useNodeActivityFeed.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';

// Utilities
import { EngagementType } from 'utilities/Enums';
import { generateKey } from 'utilities/Keys';
import { canUser, PermissionAction } from 'utilities/Permissions';
import { buildUserSummary } from 'utilities/Users';
import {
  ACTIVITY_FEED_ITEM_KIND,
  buildActivityFeedItems,
} from 'node/activity/utils/buildActivityFeedItems';
import {
  indexActivityReactionsByCommentKey,
  indexActivityReactionsByEventKey,
} from 'node/activity/utils/activityReactions';

// Services
import { subscribeToEngagements } from 'services/engagements/EngagementReadService';
import {
  createEngagement,
  deleteEngagementsByParams,
  updateEngagement,
} from 'services/engagements/EngagementWriteService';
import {
  NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT,
  subscribeToNodeActivityEvents,
} from 'services/nodes/NodeActivityService';
import { nowTimestamp } from 'services/nodes/NodeWriteService';

const ACTIVITY_FEED_EVENTS_LIMIT = Math.max(50, NODE_ACTIVITY_EVENTS_LIMIT_DEFAULT);

/**
 * Dual-subscribes to comments + system activity and exposes Activity feed state.
 *
 * @param {object|null|undefined} node
 * @param {{ draftAttachmentNodeKeys?: string[] }} [options]
 * @returns {object}
 */
export default function useNodeActivityFeed(node, options = {}) {
  const { user } = useUser();
  const { getType } = useNodeTypes();
  const draftAttachmentNodeKeys = options?.draftAttachmentNodeKeys;

  const [comments, setComments] = useState([]);
  const [activityReactions, setActivityReactions] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [showReplies, setShowReplies] = useState({});
  const [expandedReplies, setExpandedReplies] = useState({});

  const nodeKey = typeof node?.key === 'string' ? node.key.trim() : '';

  const canComment = useMemo(() => {
    if (!node || !user) return false;
    return canUser(PermissionAction.COMMENT, node, user);
  }, [node, user]);

  useEffect(() => {
    setShowReplies({});
    setExpandedReplies({});
    setReplyingTo(null);
    setSelectedComment(null);
    setConfirmDeleteOpen(false);
  }, [nodeKey]);

  useEffect(() => {
    if (!nodeKey) {
      setComments([]);
      setActivityReactions([]);
      setLoadingComments(false);
      return undefined;
    }

    setLoadingComments(true);
    const params = [{ field: 'itemKey', operator: '==', value: nodeKey }];
    const unsubscribe = subscribeToEngagements({ params, sortFields: [] }, (engagements) => {
      const list = Array.isArray(engagements) ? engagements : [];
      setComments(
        list
          .filter((engagement) => engagement.type === EngagementType.NODE_COMMENT)
          .map((comment) => ({ ...comment }))
      );
      setActivityReactions(list.filter((engagement) => (
        engagement.type === EngagementType.ACTIVITY_REACTION
        || engagement.type === EngagementType.COMMENT_LIKE
      )));
      setLoadingComments(false);
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [nodeKey]);

  useEffect(() => {
    if (!nodeKey) {
      setEvents([]);
      setLoadingEvents(false);
      setError(null);
      return undefined;
    }

    setLoadingEvents(true);
    setError(null);

    const unsubscribe = subscribeToNodeActivityEvents(
      { nodeKey, limit: ACTIVITY_FEED_EVENTS_LIMIT },
      (nextEvents) => {
        setEvents(Array.isArray(nextEvents) ? nextEvents : []);
        setLoadingEvents(false);
      },
      (err) => {
        console.warn('[useNodeActivityFeed] activity subscribe failed:', err);
        setEvents([]);
        setError('We could not load recent activity.');
        setLoadingEvents(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [nodeKey]);

  const typeLabel = useMemo(() => {
    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    if (!typeKey) return '';
    return String(getType(typeKey)?.label || '').trim();
  }, [getType, node?.type]);

  const feedItems = useMemo(() => (
    buildActivityFeedItems({
      comments,
      events,
      typeLabel,
      draftAttachmentNodeKeys,
      collapseDetails: true,
    })
  ), [comments, draftAttachmentNodeKeys, events, typeLabel]);

  const topLevelComments = useMemo(() => (
    comments.filter((comment) => (
      (comment?.parentKey == null || comment?.parentKey === '')
    ))
  ), [comments]);

  const reactionsByEventKey = useMemo(
    () => indexActivityReactionsByEventKey(activityReactions),
    [activityReactions]
  );

  const reactionsByCommentKey = useMemo(
    () => indexActivityReactionsByCommentKey(activityReactions),
    [activityReactions]
  );

  const repliesByOriginalKey = useMemo(() => {
    const map = new Map();
    topLevelComments.forEach((comment) => {
      const replies = comments
        .filter((entry) => entry.originalKey === comment.key && entry.parentKey != null)
        .sort((a, b) => {
          const aMs = a?.dateCreated?.toDate?.()?.getTime?.() ?? 0;
          const bMs = b?.dateCreated?.toDate?.()?.getTime?.() ?? 0;
          return aMs - bMs;
        });
      map.set(comment.key, replies);
    });
    return map;
  }, [comments, topLevelComments]);

  const addComment = useCallback(async (text, replyTarget = null, attachments = []) => {
    const body = String(text || '').trim();
    const safeAttachments = Array.isArray(attachments) ? attachments : [];
    if (!body && safeAttachments.length === 0) return false;
    if (!user?.key || !nodeKey) return false;

    const target = replyTarget || replyingTo;
    const key = generateKey();
    const now = nowTimestamp();
    const originalKey = target ? (target.originalKey || target.key) : key;
    const userSummary = buildUserSummary(user);

    const data = {
      key,
      userKey: user.key,
      user: userSummary,
      coverUrl: userSummary?.coverUrl || null,
      coverStoragePath: userSummary?.coverStoragePath || null,
      storagePath: userSummary?.storagePath || null,
      itemKey: nodeKey,
      itemSummary: node,
      parentKey: target ? target.key : null,
      originalKey,
      targetKey: node?.userKey || null,
      text: body,
      type: EngagementType.NODE_COMMENT,
      dateCreated: now,
    };

    if (safeAttachments.length > 0) {
      data.attachments = safeAttachments;
    }

    await createEngagement({ key, engagement: data });

    if (target) {
      const markerKey = generateKey();
      const {
        attachments: _attachments,
        ...markerBase
      } = data;
      await createEngagement({
        key: markerKey,
        engagement: {
          ...markerBase,
          key: markerKey,
          // Marker engagements omit attachments; keep non-empty text for rules.
          text: body || ' ',
          targetKey: target.userKey,
          type: EngagementType.COMMENT_REPLY,
        },
      });
    }

    setReplyingTo(null);
    return true;
  }, [node, nodeKey, replyingTo, user]);

  const requestDeleteComment = useCallback((comment) => {
    setSelectedComment(comment || null);
    setConfirmDeleteOpen(Boolean(comment));
  }, []);

  const confirmDeleteComment = useCallback(async () => {
    setConfirmDeleteOpen(false);
    if (!selectedComment || !user?.key || !nodeKey) return;

    const hasReplies = comments.some(
      (entry) => entry.originalKey === selectedComment.originalKey && entry.parentKey != null
    );

    if (hasReplies) {
      await updateEngagement(selectedComment.key, {
        ...selectedComment,
        text: '[comment deleted]',
        dateModified: nowTimestamp(),
        deleted: true,
      });
    } else {
      await deleteEngagementsByParams(
        [
          { field: 'type', operator: '==', value: EngagementType.NODE_COMMENT },
          { field: 'userKey', operator: '==', value: user.key },
          { field: 'itemKey', operator: '==', value: nodeKey },
        ],
        { engagementKey: selectedComment.key }
      );
    }

    setSelectedComment(null);
  }, [comments, nodeKey, selectedComment, user?.key]);

  const toggleReplies = useCallback((commentKey) => {
    setShowReplies((prev) => ({ ...prev, [commentKey]: !prev[commentKey] }));
  }, []);

  const toggleMoreReplies = useCallback((commentKey) => {
    setExpandedReplies((prev) => ({ ...prev, [commentKey]: !prev[commentKey] }));
  }, []);

  const beginReply = useCallback((comment) => {
    setReplyingTo(comment || null);
  }, []);

  const cancelReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  return {
    feedItems,
    comments,
    commentReactions: activityReactions,
    reactionsByCommentKey,
    reactionsByEventKey,
    repliesByOriginalKey,
    showReplies,
    expandedReplies,
    loading: Boolean(nodeKey) && (loadingComments || loadingEvents),
    error,
    canComment,
    replyingTo,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    addComment,
    requestDeleteComment,
    confirmDeleteComment,
    toggleReplies,
    toggleMoreReplies,
    beginReply,
    cancelReply,
    ACTIVITY_FEED_ITEM_KIND,
  };
}
